import React, { useState } from 'react';
import { Users, Calendar, MapPin, Clock, Heart, Share2, Filter, Search, TrendingUp, Star } from 'lucide-react';

const ConceptEvents = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // grid or list

  const categories = [
    { id: 'all', label: 'All Events', count: 47 },
    { id: 'social', label: 'Social', count: 15 },
    { id: 'sports', label: 'Sports', count: 12 },
    { id: 'culture', label: 'Culture', count: 8 },
    { id: 'food', label: 'Food & Drink', count: 7 },
    { id: 'outdoor', label: 'Outdoor', count: 5 }
  ];

  const events = [
    {
      id: 1,
      title: 'Sunday Brunch at Paradeplatz',
      category: 'food',
      date: 'Nov 8',
      time: '11:00 AM',
      location: 'Zurich',
      host: 'Sarah M.',
      attendees: 12,
      maxAttendees: 15,
      image: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=400&h=300&fit=crop',
      featured: true,
      description: 'Join us for a casual brunch and meet new people!'
    },
    {
      id: 2,
      title: 'Hiking Pilatus Mountain',
      category: 'outdoor',
      date: 'Nov 9',
      time: '7:00 AM',
      location: 'Lucerne',
      host: 'Marco R.',
      attendees: 8,
      maxAttendees: 12,
      image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=400&h=300&fit=crop',
      featured: true,
      description: 'Sunrise hike with breathtaking views!'
    },
    {
      id: 3,
      title: 'Board Game Night',
      category: 'social',
      date: 'Nov 10',
      time: '7:00 PM',
      location: 'Bern',
      host: 'Lisa K.',
      attendees: 6,
      maxAttendees: 10,
      image: 'https://images.unsplash.com/photo-1610890716171-6b1bb98ffd09?w=400&h=300&fit=crop',
      featured: false,
      description: 'Bring your favorite games or try something new!'
    },
    {
      id: 4,
      title: 'Photography Walk Old Town',
      category: 'culture',
      date: 'Nov 11',
      time: '2:00 PM',
      location: 'Basel',
      host: 'Tom W.',
      attendees: 15,
      maxAttendees: 20,
      image: 'https://images.unsplash.com/photo-1452587925148-ce544e77e70d?w=400&h=300&fit=crop',
      featured: false,
      description: 'Capture Basel\'s beautiful architecture together'
    },
    {
      id: 5,
      title: 'Beach Volleyball Tournament',
      category: 'sports',
      date: 'Nov 12',
      time: '3:00 PM',
      location: 'Geneva',
      host: 'Alex P.',
      attendees: 20,
      maxAttendees: 24,
      image: 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=400&h=300&fit=crop',
      featured: true,
      description: 'Friendly tournament by the lake!'
    },
    {
      id: 6,
      title: 'Wine Tasting Evening',
      category: 'food',
      date: 'Nov 13',
      time: '6:30 PM',
      location: 'Lausanne',
      host: 'Sophie D.',
      attendees: 10,
      maxAttendees: 12,
      image: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&h=300&fit=crop',
      featured: false,
      description: 'Discover Swiss wines with a sommelier'
    },
    {
      id: 7,
      title: 'Yoga in the Park',
      category: 'sports',
      date: 'Nov 14',
      time: '8:00 AM',
      location: 'Zurich',
      host: 'Nina S.',
      attendees: 18,
      maxAttendees: 25,
      image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop',
      featured: false,
      description: 'Start your day with outdoor yoga'
    },
    {
      id: 8,
      title: 'Startup Networking Mixer',
      category: 'social',
      date: 'Nov 15',
      time: '6:00 PM',
      location: 'Zurich',
      host: 'David L.',
      attendees: 35,
      maxAttendees: 50,
      image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=400&h=300&fit=crop',
      featured: true,
      description: 'Connect with entrepreneurs and innovators'
    }
  ];

  const filteredEvents = selectedFilter === 'all' 
    ? events 
    : events.filter(e => e.category === selectedFilter);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Calendar className="w-8 h-8 text-red-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">SwissMeet</h1>
                <p className="text-sm text-gray-500">Events Platform</p>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">Concept C:</span> Events-First View
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-2xl p-8 mb-8 text-white">
          <h2 className="text-3xl font-bold mb-2">Discover Events Near You</h2>
          <p className="text-red-100 mb-6">Join activities, meet new people, and explore Switzerland together</p>
          
          {/* Search Bar */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search events, activities, locations..."
                className="w-full pl-12 pr-4 py-3 rounded-xl text-gray-900 focus:ring-2 focus:ring-red-300 focus:outline-none"
              />
            </div>
            <button className="px-6 py-3 bg-white text-red-600 rounded-xl font-medium hover:bg-red-50 transition-colors flex items-center space-x-2">
              <Filter className="w-5 h-5" />
              <span>Filters</span>
            </button>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Left Sidebar - Categories */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm p-4 sticky top-6">
              <h3 className="font-bold text-gray-900 mb-3">Categories</h3>
              <div className="space-y-1">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedFilter(cat.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
                      selectedFilter === cat.id
                        ? 'bg-red-50 text-red-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span>{cat.label}</span>
                    <span className="text-sm text-gray-500">{cat.count}</span>
                  </button>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-bold text-gray-900 mb-3">Quick Filters</h3>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm text-gray-700">
                    <input type="checkbox" className="rounded text-red-600" />
                    <span>This Week</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm text-gray-700">
                    <input type="checkbox" className="rounded text-red-600" />
                    <span>Free Events</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm text-gray-700">
                    <input type="checkbox" className="rounded text-red-600" />
                    <span>Spots Available</span>
                  </label>
                </div>
              </div>

              <button className="w-full mt-6 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium">
                + Create Event
              </button>
            </div>
          </div>

          {/* Main Content - Events Grid */}
          <div className="flex-1">
            {/* Featured Events Banner */}
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Star className="w-5 h-5 mr-2 text-yellow-500" />
                Featured Events
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {events.filter(e => e.featured).slice(0, 2).map(event => (
                  <div
                    key={event.id}
                    className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer border-2 border-yellow-200"
                  >
                    <div className="relative h-48">
                      <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
                      <div className="absolute top-3 right-3 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center">
                        <Star className="w-4 h-4 mr-1" />
                        Featured
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-red-600 bg-red-50 px-3 py-1 rounded-full">
                          {event.date}
                        </span>
                        <button className="text-gray-400 hover:text-red-600">
                          <Heart className="w-5 h-5" />
                        </button>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2">{event.title}</h4>
                      <p className="text-sm text-gray-600 mb-3">{event.description}</p>
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center text-gray-600">
                          <MapPin className="w-4 h-4 mr-1" />
                          {event.location}
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Users className="w-4 h-4 mr-1" />
                          {event.attendees}/{event.maxAttendees}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* All Events */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                {selectedFilter === 'all' ? 'All Events' : categories.find(c => c.id === selectedFilter)?.label}
              </h3>
              <div className="text-sm text-gray-600">
                {filteredEvents.length} events
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEvents.map(event => (
                <div
                  key={event.id}
                  className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                >
                  <div className="relative h-40">
                    <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
                    <button className="absolute top-3 right-3 bg-white/90 p-2 rounded-full hover:bg-white">
                      <Heart className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-red-600 bg-red-50 px-2 py-1 rounded-full">
                        {event.date}
                      </span>
                      <span className="text-xs text-gray-500">{event.time}</span>
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2 line-clamp-2">{event.title}</h4>
                    <div className="space-y-1 text-sm text-gray-600 mb-3">
                      <div className="flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        {event.location}
                      </div>
                      <div className="flex items-center">
                        <Users className="w-3 h-3 mr-1" />
                        {event.attendees}/{event.maxAttendees} attending
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="text-sm text-gray-600">by {event.host}</span>
                      <button className="text-red-600 hover:text-red-700 font-medium text-sm">
                        Join →
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating Action Button */}
      <button className="fixed bottom-8 right-8 w-14 h-14 bg-red-600 text-white rounded-full shadow-lg hover:bg-red-700 transition-colors flex items-center justify-center">
        <Calendar className="w-6 h-6" />
      </button>
    </div>
  );
};

export default ConceptEvents;
